import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { Store } from 'vuex'
import { Router } from 'vue-router'
import baseconfig from '@/assets/config'
import { State as StoreState } from '@/store/index'
import * as log from '@/model/util/log/Logger'
import { useScreen } from '@/model/screen/screenTransition'
import { RouterTypeData } from '../router/router'

/**
 * リクエスト共通処理を実施する
 * @param axiosInstance axioインスタンス
 */
const reqinterceptors = (axiosInstance: AxiosInstance) => {
  // リクエストログのデバッグログを実施する
  axiosInstance.interceptors.request.use((config: AxiosRequestConfig) => {
    log.debug(
      `api send start. url:${config.url}, method:${config.method}, header: ${JSON.stringify(
        config.headers
      )}, data:${JSON.stringify(config.data)}, params:${JSON.stringify(config.params)}, timeout:${config.timeout}(ms)`
    )
    return config
  })
}

/**
 * レスポンス共通処理を実施する
 * @param axiosInstance axioインスタンス
 * @param router vueRouter
 * @param store vuex
 */
const resinterceptors = (axiosInstance: AxiosInstance, router: Router, store: Store<StoreState>) => {
  axiosInstance.interceptors.response.use(
    // レスポンス正常処理
    (response: AxiosResponse) => {
      log.debug(`api send end. status:${response.status}, data:${JSON.stringify(response.data)}`)
      return response
    },
    // レスポンス異常処理
    (error: AxiosError) => {
      // デバッグログ出力
      log.debug(`api send end. status:${error.response?.status}, data:${JSON.stringify(error.response?.data)}`)
      // 認証切れの場合はTOP画面に遷移する
      // 403 ForbiddenはCSRFトークンチェック異常を含む認証切れの可能性があるため、ログイン画面に遷移する
      if (error.response?.status === 401 || error.response?.status === 403) {
        log.debug(`Transition to login screen due to authentication expiration.`)
        const data: RouterTypeData.LoginTopData = {}
        useScreen().topRedirect(store, router, data)
      }
      // 認証エラーの場合はログイン画面に遷移する
      return Promise.reject(error)
    }
  )
}

/**
 * 非認証時のレスポンス共通処理を実施する
 * @param axiosInstance axioインスタンス
 */
const resNoauthinterceptors = (axiosInstance: AxiosInstance) => {
  axiosInstance.interceptors.response.use(
    // レスポンス正常処理
    (response: AxiosResponse) => {
      log.debug(`api send end. status:${response.status}, data:${JSON.stringify(response.data)}`)
      return response
    },
    // レスポンス異常処理
    (error: AxiosError) => {
      // デバッグログ出力
      log.debug(`api send end. status:${error.response?.status}, data:${JSON.stringify(error.response?.data)}`)
      return Promise.reject(error)
    }
  )
}
/**
 * axiosのログイン認証後の共通インスタンスを取得する
 * @param router vueRouter
 * @param store vuex
 * @returns axiosインスタンス
 */
const instanceAuth = (router: Router, store: Store<StoreState>): AxiosInstance => {
  // Accept-Language及びX-CSRF-TOKENを取得する
  const obj = axios.create({
    timeout: Number(baseconfig.api.timeout),
    headers: {
      'Accept-Language': store.getters.language,
      'X-XSRF-TOKEN': store.getters.csrfToken,
    },
    withCredentials: true,
  })
  // リクエスト共通処理設定
  reqinterceptors(obj)
  // レスポンス共通処理設定
  resinterceptors(obj, router, store)
  return obj
}

/**
 * axiosのログイン認証前の共通インスタンスを取得する
 * @param store vuex
 * @returns axiosインスタンス
 */
const instanceNoAuth = (store: Store<StoreState>): AxiosInstance => {
  // Accept-Languageを取得する
  const obj = axios.create({
    timeout: Number(baseconfig.api.timeout),
    headers: {
      'Accept-Language': store.getters.language,
    },
    withCredentials: true,
  })
  // リクエスト共通処理設定
  reqinterceptors(obj)
  // レスポンス共通処理設定
  resNoauthinterceptors(obj)
  return obj
}

export {
  instanceAuth, //
  instanceNoAuth, //
}
